import { createIsomorphicFn } from "@tanstack/react-start";
import { getCookie, getRequestHeader } from "@tanstack/react-start/server";
import {
  activeConsent,
  CONSENT_VERSION,
  type ConsentRecord,
} from "@/engine/diagnostics/consent";
import {
  CONSENT_COOKIE,
  parseConsentCookie,
} from "@/engine/support/consentCookie";
import { regionRule, type RegionRule } from "@/engine/support/consentRegions";

/** What the first render knows about consent before any script has run. */
export interface InitialConsent {
  /** The decision already on record, as far as this side can see it. */
  record: ConsentRecord | null;
  /**
   * The region's rule, or `null` where this side cannot know it — the browser
   * before its lookup has answered, or a server with no country header, which
   * is every server that is not behind Cloudflare.
   */
  rule: RegionRule | null;
}

/**
 * What is known about consent at the moment the page is rendered, on whichever
 * side of the seam is asking.
 *
 * The root route calls it as a loader, so on a served page it runs on the
 * server and its answer travels to the client as loader data. That is the whole
 * point: the consent surface is the largest element on the entry page, and
 * while it could only be decided by a script it landed after hydration and held
 * the largest paint open for seconds (#531). Decided here, it is in the first
 * frame.
 *
 * The country comes from Cloudflare's own header rather than a lookup, so it
 * costs no round trip. Where there is no header — any deployment that is not
 * behind Cloudflare, and the local stack the e2e suites drive — the rule is
 * left unknown and the browser's lookup decides, exactly as it did before.
 *
 * The client half is for the loader running again later: an invalidation, or a
 * navigation that remounts the root match, where the store is the live answer
 * and there is no request to read. `createIsomorphicFn` keeps the request API,
 * and the server half that reaches for it, out of the browser bundle.
 */
export const initialConsent = createIsomorphicFn()
  .server((): InitialConsent => {
    const cookie = parseConsentCookie(getCookie(CONSENT_COOKIE));
    const record: ConsentRecord | null =
      cookie && cookie.version >= CONSENT_VERSION
        ? {
            version: cookie.version,
            analytics: cookie.analytics,
            marketing: cookie.marketing,
            // The cookie does not carry it, and nothing reads it from here: it
            // is informational on the record and the store holds the real one.
            decidedAt: "",
          }
        : null;
    const country = getRequestHeader("cf-ipcountry");
    return { record, rule: country ? regionRule(country) : null };
  })
  .client((): InitialConsent => ({ record: activeConsent(), rule: null }));
